import type { Subscription, Reducer, Effect } from 'umi'
import { fileUpload, fileDownload, globalSearch } from '@/services/global'
import { getMessageList, messageCount, messageRead } from '@/services/interact/message'
import type { ConnectState } from './connect'
import { apiAvatarList } from '@/services/user'

export interface NewsItem {
  id: string
  is_read: number
  type: number
  content: string
}
interface IUserAvatar {
  avatar?: string
  id?: number
  name_en?: string
  name_zh?: string
}
type IUserAvatarMap = { [key: string]: IUserAvatar }
export interface GlobalModelState {
  news: NewsItem[]
  newsUnreadCount: number
  pathName: {
    last: string
    current: string
  }
  searchParams: {
    keywords: string
  }
  userAvatarMap: IUserAvatarMap
}

export interface GlobalModelType {
  namespace: 'global'
  state: GlobalModelState
  effects: {
    fetchUserAvatar: Effect
    fetchNewsUnreadCount: Effect
    fetchNews: Effect
    readNews: Effect
    changeNoticeReadState: Effect
    fetchFileUpload: Effect
    fetchFileDownload: Effect
    initGlobalSearchValue: Effect
  }
  reducers: {
    saveNews: Reducer<GlobalModelState>
    saveReadNews: Reducer<GlobalModelState>
    saveNewsUnreadCount: Reducer<GlobalModelState>
    setPathName: Reducer<GlobalModelState>
    saveGlobalSearchValue: Reducer<GlobalModelState>
    saveUserAvatarMap: Reducer<GlobalModelState>
  }
  subscriptions: { setup: Subscription }
}

const initialState = {
  news: [],
  newsUnreadCount: 0,
  pathName: {
    last: '',
    current: ''
  },
  searchParams: {
    keywords: ''
  },
  userAvatarMap: {},
}

const GlobalModel: GlobalModelType = {
  namespace: 'global',

  state: initialState,
  effects: {
    *fetchUserAvatar({ payload }, { call, put, select }) {
      const userId2Avatar: number = yield select(
        (state: ConnectState) => state.global.userAvatarMap
      )
      const existUserIds = Object.keys(userId2Avatar);
      const noExistUserIds = payload.filter((userId: string) => !existUserIds.includes(userId + ''));
      if (noExistUserIds.length > 0) {
        const { data } = yield call(apiAvatarList, { ids: noExistUserIds })
        const avatarList = data.avatar_list ?? [];
        const newId2Avatar = avatarList.reduce((pre: IUserAvatarMap, cur: IUserAvatar) => {
          const { id, ...res } = cur;
          pre[id + ''] = res
          return pre
        }, {})
        yield put({
          type: 'saveUserAvatarMap',
          payload: newId2Avatar
        })
      }
    },
    // 文件上传
    *fetchFileUpload({ payload }, { call, put }) {
      const res = yield call(fileUpload, payload)
      if (res.file_id) {
        return Promise.resolve(res.file_id)
      }
      return Promise.reject(payload)
    },
    *fetchFileDownload({ payload }, { call, put }) {
      const res = yield call(fileDownload, payload)
      if (res.code === 0) {
        return Promise.resolve(res.data)
      }
      return Promise.reject()
    },
    *fetchNews({ payload }, { call, put, select }) {
      const res = yield call(getMessageList, payload)
      yield put({
        type: 'saveNews',
        payload: res.data
      })

      if (res.code === 0) {
        return Promise.resolve(res)
      }
      return Promise.reject()
    },
    *fetchNewsUnreadCount({ payload }, { call, put }) {
      const res = yield call(messageCount, {})
      yield put({
        type: 'saveNewsUnreadCount',
        payload: {
          count: res.data.count
        }
      })
    },
    *readNews({ payload }, { put, call, select }) {
      const res = yield call(messageRead, payload)
      if (res.code == 0) {
        yield put({
          type: 'saveReadNews',
          payload: payload.msg_ids,
        })

        const unreadCount: number = yield select(
          (state: ConnectState) => state.global.newsUnreadCount
        )

        const count = unreadCount - payload.msg_ids.length

        yield put({
          type: 'saveNewsUnreadCount',
          payload: {
            count: count >= 0 ? count : unreadCount
          }
        })

        return Promise.resolve(res.data)
      }
      return Promise.reject()
    }
  },

  reducers: {
    saveNews(state = initialState, { payload }): GlobalModelState {
      return {
        ...state,
        news: payload.list
      }
    },
    saveReadNews(state = initialState, { payload }): GlobalModelState {
      const temp = state.news

      temp.forEach(item => {
        if (payload.indexOf(item.id)) {
          item.is_read = 1
        }
      })

      return {
        ...state,
        news: temp
      }
    },
    saveNewsUnreadCount(state = initialState, { payload }): GlobalModelState {
      return {
        ...state,
        newsUnreadCount: payload.count
      }
    },
    setPathName(state = initialState, { payload }): GlobalModelState {
      return {
        ...state,
        pathName: {
          last: state.pathName.current,
          current: payload
        }
      }
    },
    saveGlobalSearchValue(state = initialState, { payload }): GlobalModelState {
      return {
        ...state,
        searchParams: {
          ...payload
        }
      }
    },
    saveUserAvatarMap(state = initialState, { payload }): GlobalModelState {
      const { userAvatarMap } = state
      return {
        ...state,
        userAvatarMap: {
          ...userAvatarMap,
          ...payload,
        }
      }
    }
  },

  subscriptions: {
    setup({ history, dispatch }): void {
      // Subscribe history(url) change, trigger `load` action if pathname is `/`
      history.listen(({ pathname, search }): void => {
        dispatch({ type: 'setPathName', payload: pathname })
        if (typeof window.ga !== 'undefined') {
          window.ga('send', 'pageview', pathname + search)
        }
      })
    }
  }
}

export default GlobalModel
